import { useState, useRef, cloneElement } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, Loader2, ChevronRight, ChevronDown, Zap, FileText } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";
import { useIsMobile } from "@/hooks/useIsMobile";
import { TaskDrawer } from "@/components/tasks/TaskDrawer";
import { DatePicker } from "@/components/tasks/DateTimePicker";

const PRIORITIES = ["low", "normal", "high", "urgent"];

// One reusable Create Task experience. Compact Quick Create renders through the
// portal-based overlay system (Popover on desktop, bottom Sheet on mobile) so it
// is never clipped by a scrolling board column. "More options" hands everything
// off to the full Create drawer. `variant="split"` renders the New Task split
// button; otherwise the caller supplies its own `trigger`.
export function TaskQuickCreate({ trigger, variant = "trigger", defaults = {}, cats = [], boards = [], members = [], canAssign = false, permsLoading = false, onCreated, align = "start" }) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [full, setFull] = useState(false);
  const [form, setForm] = useState(blank(defaults));
  const [busy, setBusy] = useState(false);
  const guardUntil = useRef(0);   // ignore popover auto-close briefly after opening from the menu

  const hasContent = () => form.title.trim() || form.due_date || form.owner_id;
  const reset = () => setForm(blank(defaults));
  const cancel = () => { reset(); setOpen(false); };
  // Safe dismiss: nothing meaningful entered → discard; otherwise preserve.
  const onOverlayChange = (next) => {
    if (!next && Date.now() < guardUntil.current) return;   // phantom close from the just-closed DropdownMenu
    if (!next && !hasContent()) reset();
    setOpen(next);
  };

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Task title is required.");
    setBusy(true);
    try {
      const body = { ...defaults, title: form.title.trim(), due_date: form.due_date || null,
        priority: form.priority, task_type: form.owner_id ? "assigned" : (defaults.task_type || "personal"),
        owner_id: form.owner_id || undefined };
      const t = await apiFetch("/tasks", { method: "POST", body: JSON.stringify(body) });
      toast.success("Task created"); onCreated?.(t); setOpen(false); reset();
    } catch (e) { toast.error("Could not create task", { description: e.message }); } finally { setBusy(false); }
  };

  const openQuick = () => setOpen(true);
  // Opening from inside the DropdownMenu: set a short guard window so the menu's
  // closing "interact outside" doesn't immediately dismiss the popover.
  const openFromMenu = () => setTimeout(() => { guardUntil.current = Date.now() + 400; setOpen(true); }, 0);
  const openFull = () => { setOpen(false); setFull(true); };   // carry values, no reset

  const body = (
    <div className="space-y-3" data-testid="quick-create">
      {isMobile && <div className="mb-1 flex items-center gap-2"><Zap className="h-4 w-4 text-accent" /><span className="font-heading text-base font-semibold">New task</span></div>}
      <div className="space-y-1.5">
        <label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/80" htmlFor="qc-title">Task title <span className="text-destructive">*</span></label>
        <Input id="qc-title" autoFocus={!isMobile} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); submit(); } }}
          placeholder="What needs doing?" data-testid="qc-title" />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70">Due date</label>
          <DatePicker value={form.due_date} onChange={(v) => setForm({ ...form, due_date: v })} placeholder="Set due date" testid="qc-due" side="right" align="center" sideOffset={14} />
        </div>
        <div className="space-y-1">
          <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70">Priority</label>
          <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
            <SelectTrigger className="h-9" data-testid="qc-priority"><SelectValue /></SelectTrigger>
            <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      {(canAssign || permsLoading) && (
        <div className="space-y-1">
          <label className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground/70">Assignee</label>
          <Select value={form.owner_id || "self"} onValueChange={(v) => setForm({ ...form, owner_id: v === "self" ? "" : v })} disabled={permsLoading}>
            <SelectTrigger className="h-9" data-testid="qc-assignee"><SelectValue placeholder={permsLoading ? "Loading…" : undefined} /></SelectTrigger>
            <SelectContent><SelectItem value="self">Myself</SelectItem>{members.map((m) => <SelectItem key={m.id} value={m.id}>{m.full_name || m.login_email}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
      <button onClick={openFull} className="flex items-center gap-1 text-xs font-medium text-accent hover:underline" data-testid="qc-more">
        More options <ChevronRight className="h-3.5 w-3.5" />
      </button>
      <div className="flex items-center justify-end gap-2 border-t border-border/60 pt-3">
        <Button variant="ghost" size="sm" onClick={cancel} data-testid="qc-cancel">Cancel</Button>
        <Button size="sm" onClick={submit} disabled={busy} className="gap-1.5" data-testid="qc-save">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Add Task
        </Button>
      </div>
    </div>
  );

  const splitButton = (
    <div className="inline-flex items-stretch overflow-hidden rounded-md shadow-sm">
      <Button className="gap-1.5 rounded-r-none" onClick={isMobile ? openQuick : undefined} data-testid="new-task-btn"><Plus className="h-4 w-4" /> New Task</Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button className="rounded-l-none border-l border-primary-foreground/25 px-2" data-testid="new-task-menu" aria-label="Task creation options"><ChevronDown className="h-4 w-4" /></Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44">
          <DropdownMenuItem onClick={openFromMenu} data-testid="new-task-quick"><Zap className="mr-2 h-4 w-4" /> Quick Task</DropdownMenuItem>
          <DropdownMenuItem onClick={openFull} data-testid="new-task-full"><FileText className="mr-2 h-4 w-4" /> Full Task</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  const fullDrawer = full && (
    <TaskDrawer mode="create" initial={form} defaults={defaults} cats={cats} boards={boards} members={members} canAssign={canAssign}
      onClose={() => { setFull(false); reset(); }}
      onCreated={(t) => { setFull(false); reset(); onCreated?.(t); }} />
  );

  // MOBILE — bottom sheet (Quick), full-screen drawer for "More options".
  if (isMobile) {
    const opener = variant === "split" ? splitButton : (trigger ? cloneElement(trigger, { onClick: openQuick }) : null);
    return (
      <>
        {opener}
        <Sheet open={open} onOpenChange={onOverlayChange}>
          <SheetContent side="bottom" className="max-h-[88vh] overflow-y-auto rounded-t-2xl pb-8">
            <SheetHeader className="sr-only"><SheetTitle>New task</SheetTitle></SheetHeader>
            {body}
          </SheetContent>
        </Sheet>
        {fullDrawer}
      </>
    );
  }

  // DESKTOP / TABLET — floating popover anchored to the trigger.
  return (
    <>
      <Popover open={open} onOpenChange={onOverlayChange}>
        {variant === "split" ? (
          <div className="inline-flex items-stretch overflow-hidden rounded-md shadow-sm">
            <PopoverTrigger asChild><Button className="gap-1.5 rounded-r-none" data-testid="new-task-btn"><Plus className="h-4 w-4" /> New Task</Button></PopoverTrigger>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="rounded-l-none border-l border-primary-foreground/25 px-2" data-testid="new-task-menu" aria-label="Task creation options"><ChevronDown className="h-4 w-4" /></Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuItem onClick={openFromMenu} data-testid="new-task-quick"><Zap className="mr-2 h-4 w-4" /> Quick Task</DropdownMenuItem>
                <DropdownMenuItem onClick={openFull} data-testid="new-task-full"><FileText className="mr-2 h-4 w-4" /> Full Task</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ) : (
          <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        )}
        <PopoverContent align={align} sideOffset={8} onInteractOutside={(e) => { if (Date.now() < guardUntil.current) e.preventDefault(); }} className="z-drawer w-[340px] p-4">{body}</PopoverContent>
      </Popover>
      {fullDrawer}
    </>
  );
}

function blank(defaults) {
  return { title: "", due_date: defaults.due_date || "", priority: defaults.priority || "normal", owner_id: defaults.owner_id || "" };
}
